import React, { useRef } from "react";

function FilePicker({ label, accept, file, existingName, onPick, onClear, helper, disabled }) {
  const inputRef = useRef(null);
  const currentLabel = file ? file.name : existingName || "Aucun fichier choisi";

  return (
    <div className="form-row">
      <label className="label">{label}</label>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        disabled={disabled}
        onChange={(e) => {
          const nextFile = e.target.files?.[0] || null;
          onPick(nextFile);
          e.target.value = "";
        }}
      />

      <div
        onClick={() => !disabled && inputRef.current?.click()}
        style={{
          border: "1px dashed rgba(255,255,255,0.22)",
          borderRadius: 14,
          padding: 12,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          type="button"
          className="btn"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            inputRef.current?.click();
          }}
          style={{ padding: "6px 10px", borderRadius: 10 }}
        >
          Choisir un fichier
        </button>

        <div style={{ fontSize: 13, opacity: 0.9 }}>{currentLabel}</div>

        {(file || existingName) && (
          <button
            type="button"
            className="btn"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClear?.();
            }}
            style={{ marginLeft: "auto", padding: "6px 10px", borderRadius: 10 }}
          >
            Retirer
          </button>
        )}
      </div>

      {helper ? <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>{helper}</div> : null}
    </div>
  );
}

function ArchiveToggle({ checked, onChange, disabled, label }) {
  return (
    <label className="label" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export default function PcbUploadFields({
  format,
  disabled,
  onFormatChange,
  formatInputName,
  requiredMark = "",
  archiveFile,
  archiveExistingName,
  onArchivePick,
  onArchiveClear,
  supportFiles,
  getExistingName,
  onSupportFilePick,
  onSupportFileClear,
  includeInArchive,
  onArchiveToggle,
  notes,
  onNotesChange,
}) {
  const isOdb = format === "odb";
  const archiveHelper = isOdb
    ? "En mode ODB++, tous les fichiers de production nécessaires doivent être inclus dans cette archive unique."
    : "";

  return (
    <>
      <div className="form-row" style={{ marginBottom: 16 }}>
        <label className="label">Format</label>
        <div style={{ display: "flex", gap: 24, marginTop: 6 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="radio"
              name={formatInputName}
              value="gerber"
              checked={format === "gerber"}
              disabled={disabled}
              onChange={() => onFormatChange("gerber")}
            />
            Gerber (.zip)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="radio"
              name={formatInputName}
              value="odb"
              checked={format === "odb"}
              disabled={disabled}
              onChange={() => onFormatChange("odb")}
            />
            ODB++ (.zip / .tgz)
          </label>
        </div>
      </div>

      <FilePicker
        label={isOdb ? `Fichier ODB++ (ZIP ou TGZ)${requiredMark}` : `Fichier Gerber (ZIP)${requiredMark}`}
        accept={isOdb ? ".zip,.tgz,.tar.gz,application/zip,application/gzip" : ".zip,application/zip"}
        file={archiveFile}
        existingName={archiveExistingName}
        disabled={disabled}
        onPick={onArchivePick}
        onClear={onArchiveClear}
        helper={archiveHelper}
      />

      {!isOdb ? (
        <>
          <FilePicker
            label="Pick and place"
            accept=".csv,.txt,.xlsx,.xls,.zip"
            file={supportFiles.pick_place_file}
            existingName={getExistingName("pick_place_file")}
            disabled={disabled}
            onPick={(file) => onSupportFilePick("pick_place_file", file)}
            onClear={() => onSupportFileClear("pick_place_file")}
          />
          <ArchiveToggle
            checked={includeInArchive.pick_place_in_archive}
            disabled={disabled}
            onChange={(next) => onArchiveToggle("pick_place_in_archive", next)}
            label="Le pick and place est déjà inclus dans mon archive Gerber"
          />

          <FilePicker
            label="Plan d'implantation"
            accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx,.xls,.zip"
            file={supportFiles.assembly_plan_file}
            existingName={getExistingName("assembly_plan_file")}
            disabled={disabled}
            onPick={(file) => onSupportFilePick("assembly_plan_file", file)}
            onClear={() => onSupportFileClear("assembly_plan_file")}
          />
          <ArchiveToggle
            checked={includeInArchive.assembly_plan_in_archive}
            disabled={disabled}
            onChange={(next) => onArchiveToggle("assembly_plan_in_archive", next)}
            label="Le plan d'implantation est déjà inclus dans mon archive Gerber"
          />

          <FilePicker
            label="PASTE TOP"
            accept=".gtp,.gpt,.ger,.gbr,.zip"
            file={supportFiles.paste_top}
            existingName={getExistingName("paste_top")}
            disabled={disabled}
            onPick={(file) => onSupportFilePick("paste_top", file)}
            onClear={() => onSupportFileClear("paste_top")}
          />
          <ArchiveToggle
            checked={includeInArchive.paste_top_in_archive}
            disabled={disabled}
            onChange={(next) => onArchiveToggle("paste_top_in_archive", next)}
            label="Le paste top est déjà inclus dans mon archive Gerber"
          />

          <FilePicker
            label="PASTE BOT"
            accept=".gbp,.gpb,.ger,.gbr,.zip"
            file={supportFiles.paste_bot}
            existingName={getExistingName("paste_bot")}
            disabled={disabled}
            onPick={(file) => onSupportFilePick("paste_bot", file)}
            onClear={() => onSupportFileClear("paste_bot")}
          />
          <ArchiveToggle
            checked={includeInArchive.paste_bot_in_archive}
            disabled={disabled}
            onChange={(next) => onArchiveToggle("paste_bot_in_archive", next)}
            label="Le paste bot est déjà inclus dans mon archive Gerber"
          />

          <FilePicker
            label="Fichier legacy placement / implantation"
            accept=".pdf,.csv,.xlsx,.xls,.xlsm,.txt,.zip"
            file={supportFiles.placement_file}
            existingName={getExistingName("placement_file")}
            disabled={disabled}
            onPick={(file) => onSupportFilePick("placement_file", file)}
            onClear={() => onSupportFileClear("placement_file")}
          />
        </>
      ) : null}

      <div className="form-row" style={{ gridColumn: "1 / -1" }}>
        <label className="label">Commentaire production</label>
        <textarea
          value={notes}
          disabled={disabled}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Precisions utiles pour la production, les fichiers inclus ou les exceptions."
          rows={4}
          style={{ width: "100%", borderRadius: 14, padding: 12, background: "rgba(255,255,255,0.04)", color: "#fff", border: "1px solid rgba(255,255,255,0.12)" }}
        />
      </div>
    </>
  );
}
